import { t, Trans } from '@lingui/macro'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    TextFieldProps,
    Typography,
    useMediaQuery
} from '@mui/material'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { TrackingActionTypes } from '@om1/platform-tracking'
import { ActionWithPayload } from '@om1/platform-utils'
import {
    ChangeEventHandler,
    Dispatch,
    FunctionComponent,
    MouseEventHandler,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { DebounceInput } from 'react-debounce-input'
import { DeleteCohortDialog } from '../../components/list/DeleteCohortDialog'
import { cohortCommonActions, cohortDeleteActions, cohortListActions, CohortListItem, CohortLoading, ReportAnalyticsType } from '../../state'
import { DashboardCohortOwner } from './CohortDashboardPageComponent'
import { DashboardCohortCard } from './DashboardCohortCard'

const ErrorCard = (props: PropsWithChildren<{}>) => {
    return (
        <Box
            sx={{
                borderRadius: '2px', // Rounded corners
                boxShadow: 'none', // No shadow
                border: '1px solid #E0E0E0', // Border style
                backgroundColor: '#F3F4F6', // Background color when selected,
                color: '#E99689',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '15px'
            }}
        >
            {props.children}
        </Box>
    )
}

export interface DashboardCohortListProps {
    actions: Pick<typeof cohortListActions, 'cohortDuplicate'> &
        Pick<typeof cohortDeleteActions, 'cohortDelete'> &
        Pick<typeof cohortCommonActions, 'cohortSizeGet'>
    cohortList: CohortListItem[]
    cohortSizeLoading: CohortLoading
    datasetsList: CohortListItem[]
    searchValue: string | undefined
    handleSearchChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    selectedCohort?: CohortListItem
    selectedOwner?: DashboardCohortOwner
    onCreateCohortClick?: MouseEventHandler<HTMLButtonElement> | undefined
    onUpdateCohort?: (updatedCohort: CohortListItem) => void
    reportType: ReportAnalyticsType
    cohortsLoading: boolean | undefined
    setCohortId: Dispatch<SetStateAction<string | undefined>>
    setCohortOwner: Dispatch<SetStateAction<DashboardCohortOwner | undefined>>
    trackPage: (route: string) => ActionWithPayload<
        TrackingActionTypes.TRACK_PAGE,
        {
            location: {
                pathname: string
                search: string
                hash: string
                key: string
            }
            action: string
            isFirstRendering: boolean
        }
    >
    page: number
    setPage: Dispatch<SetStateAction<number>>
    totalPages: number
}

export const DashboardCohortList: FunctionComponent<DashboardCohortListProps> = (props) => {
    const isSmallScreen = useMediaQuery('(max-width:500px)') // Show dropdown below 500px

    // Responsive search width
    const searchWidth = isSmallScreen ? '95%' : '80%'
    const {
        selectedCohort,
        cohortList,
        datasetsList,
        selectedOwner,
        searchValue,
        cohortSizeLoading,
        cohortsLoading,
        setCohortId,
        setCohortOwner,
        page,
        setPage,
        totalPages
    } = props

    // A ref to scrollable div where the cohorts live
    const cohortListRef = useRef<HTMLDivElement | null>(null)
    // A ref to the currently selected cohort in the list
    const selectedCohortRef = useRef<HTMLDivElement | null>(null)
    // The last cohort id we scrolled to, stored to prevent multiple scrolls
    const lastScrolledCohortId = useRef<string | undefined>(undefined)
    // The auto scroll element that triggers loading the next page of cohorts
    const autoScrollRef = useRef<HTMLDivElement | null>(null)

    const [deleteModalCohort, setDeleteModalCohort] = useState<CohortListItem>()

    // Responsive: container-aware switch between toggle group and dropdown
    const toggleContainerRef = useRef<HTMLDivElement | null>(null)
    const toggleMeasureRef = useRef<HTMLDivElement | null>(null)
    const [useDropdown, setUseDropdown] = useState(false)

    useEffect(() => {
        const container = toggleContainerRef.current
        if (!container) return

        const measure = () => {
            const containerWidth = container.clientWidth
            const requiredWidth = toggleMeasureRef.current?.scrollWidth ?? 0
            setUseDropdown(requiredWidth > containerWidth)
        }

        const ro = new ResizeObserver(measure)
        ro.observe(container)
        // initial measure after paint
        requestAnimationFrame(measure)
        return () => {
            ro.disconnect()
        }
    }, [])

    enum DatasetCategory {
        NETWORK = 'NETWORK',
        CONDITION = 'CONDITION'
    }

    const [datasetCategory, setDatasetCategory] = useState<DatasetCategory>(DatasetCategory.CONDITION)

    const isNetworkName = useCallback((name: string) => {
        const lower = (name || '').toLowerCase()
        return lower.includes('network')
    }, [])

    const isRWDCName = useCallback((name: string) => {
        const lower = (name || '').toLowerCase()
        return lower.includes('real world data cloud') || lower.includes('rwdc')
    }, [])

    const networkDatasets = useMemo(() => datasetsList.filter((c) => isNetworkName(c.name)), [datasetsList, isNetworkName])
    const conditionDatasets = useMemo(() => {
        const items = datasetsList.filter((c) => !isNetworkName(c.name))
        // Ensure RWDC appears at the top of the Registry list
        return items.sort((a, b) => {
            const aIsRWDC = isRWDCName(a.name)
            const bIsRWDC = isRWDCName(b.name)
            if (aIsRWDC && !bIsRWDC) return -1
            if (!aIsRWDC && bIsRWDC) return 1
            return 0
        })
    }, [datasetsList, isNetworkName, isRWDCName])

    const cohorts = useMemo(() => {
        if (selectedOwner === DashboardCohortOwner.DATASET) {
            return datasetCategory === DatasetCategory.NETWORK ? networkDatasets : conditionDatasets
        }
        return cohortList
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOwner, datasetCategory, networkDatasets, conditionDatasets, cohortList])

    const filteredCohorts = useMemo(() => {
        if (searchValue) {
            return cohorts.filter(
                (cohort) =>
                    cohort.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                    cohort.createdByEmail?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    cohort.analyticsDataset?.name.toLowerCase().includes(searchValue.toLowerCase())
            )
        }

        return cohorts
    }, [searchValue, cohorts])

    const visibleCohorts = useMemo(() => {
        if (searchValue) {
            return filteredCohorts
        }
        return cohorts
    }, [searchValue, filteredCohorts, cohorts])

    // Scroll to the currently selected cohort, if it isn't in the current view of the cohort list
    useEffect(() => {
        if (!selectedCohort || visibleCohorts.findIndex((x) => x.id === selectedCohort.id) === -1) {
            return
        }
        if (selectedCohort.id === lastScrolledCohortId.current) {
            return
        }
        if (selectedCohortRef.current && cohortListRef.current) {
            lastScrolledCohortId.current = selectedCohort.id
            const { top: cardTop, bottom: cardBottom } = selectedCohortRef.current.getBoundingClientRect()
            const { top: listTop, bottom: listBottom } = cohortListRef.current.getBoundingClientRect()
            const listScrollTop = cohortListRef.current.scrollTop
            const listScrollBottom = listScrollTop + listBottom - listTop
            const cardScrollTop = cardTop - listTop + listScrollTop
            const cardScrollBottom = cardScrollTop + cardBottom - cardTop

            if (cardScrollTop < listScrollTop || cardScrollBottom > listScrollBottom) {
                cohortListRef.current.scroll({
                    behavior: 'smooth',
                    top: cardScrollTop
                })
            }
        }
    }, [selectedCohort, visibleCohorts])

    const SearchField: (props: TextFieldProps) => JSX.Element = (props: TextFieldProps) => {
        const inputRef = useRef<HTMLInputElement>(null)

        useEffect(() => {
            if (props.value) {
                inputRef.current?.focus({ preventScroll: true })
            }
        }, [props.value])

        return (
            <TextField
                {...props}
                inputRef={inputRef}
                type='search'
                placeholder={props.placeholder || t`Search`}
                sx={{ backgroundColor: '#FFFFFF', border: 'none', width: '100%', marginBottom: '10px' }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position='end'>
                            <SearchIcon sx={{ color: 'grey', width: '40px', height: '40px' }} />
                        </InputAdornment>
                    )
                }}
            />
        )
    }

    useEffect(() => {
        let observer: IntersectionObserver | null = null
        if (autoScrollRef.current && !cohortsLoading) {
            observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && page < totalPages) {
                    setPage(page + 1)
                }
            })
            observer.observe(autoScrollRef.current)
        }
        return () => {
            if (observer) {
                observer.disconnect()
            }
        }
    }, [page, setPage, cohortsLoading, totalPages])

    const [toggleValue, setToggleValue] = useState<'NETWORK' | 'CONDITION' | 'CUSTOM'>(
        selectedOwner === DashboardCohortOwner.CUSTOM ? 'CUSTOM' : 'NETWORK'
    )

    useEffect(() => {
        if (selectedOwner === DashboardCohortOwner.CUSTOM) {
            setToggleValue('CUSTOM')
        } else {
            setToggleValue(datasetCategory === DatasetCategory.NETWORK ? 'NETWORK' : 'CONDITION')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOwner, datasetCategory])

    const handleToggleChange = (_event: any, newValue: 'NETWORK' | 'CONDITION' | 'CUSTOM' | null) => {
        if (!newValue) return
        handleCohortTypeChange(newValue)
    }

    const handleDropdownChange = (event: any) => {
        const newValue = event.target.value as 'NETWORK' | 'CONDITION' | 'CUSTOM'
        handleCohortTypeChange(newValue)
    }

    const handleCohortTypeChange = (newValue: 'NETWORK' | 'CONDITION' | 'CUSTOM') => {
        setToggleValue(newValue)

        if (newValue === 'CUSTOM') {
            setCohortOwner(DashboardCohortOwner.CUSTOM)
            // Set to first custom cohort if available
            if (cohortList.length > 0) {
                setCohortId(cohortList[0].id)
            } else {
                setCohortId(undefined)
            }
        } else {
            setCohortOwner(DashboardCohortOwner.DATASET)
            const newCategory = newValue === 'NETWORK' ? DatasetCategory.NETWORK : DatasetCategory.CONDITION
            setDatasetCategory(newCategory)

            // Set to first cohort in the new category
            const targetCohorts = newValue === 'NETWORK' ? networkDatasets : conditionDatasets
            if (targetCohorts.length > 0) {
                setCohortId(targetCohorts[0].id)
            } else {
                setCohortId(undefined)
            }
        }
    }

    const handleCohortDuplicate = useCallback(
        (cohortId: string, shouldUnmarkFeatured?: boolean) => {
            // Pass the shouldUnmarkFeatured flag to indicate if the duplicate should be unfeatured
            props.actions.cohortDuplicate({
                cohortId,
                shouldUnmarkFeatured: shouldUnmarkFeatured || false
            })
        },
        [props.actions]
    )
    const handleDeleteView = useCallback(
        (id: string) => setDeleteModalCohort(props.cohortList.find((cohort) => cohort.id === id)),
        [props.cohortList]
    )
    const handleDeleteHide = useCallback(() => setDeleteModalCohort(undefined), [])
    const handleRefreshCount = useCallback(
        (id: string, name: string) => {
            props.actions.cohortSizeGet({ cohortId: id, cohortName: name })
        },
        [props.actions]
    )

    return (
        <Box height='100%'>
            <Box display='flex' flexDirection='column' height='100%' sx={{ overflow: 'hidden' }}>
                <Box
                    px={isSmallScreen ? 0.5 : 1}
                    pt={isSmallScreen ? 0.5 : 1}
                    pb={isSmallScreen ? 0.5 : 1}
                    sx={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'grey.300',
                        zIndex: 10,
                        borderBottom: '1px solid #E0E0E0'
                    }}
                >
                    <Box
                        ref={toggleContainerRef}
                        display='flex'
                        marginBottom='8'
                        height='60px'
                        alignItems='center'
                        justifyContent='center'
                        sx={{ overflow: useDropdown || isSmallScreen ? 'visible' : 'hidden' }}
                    >
                        {useDropdown || isSmallScreen ? (
                            // Dropdown for small screens
                            <FormControl
                                variant='outlined'
                                size='medium'
                                sx={{
                                    minWidth: 200,
                                    backgroundColor: '#F6F8FB',
                                    borderRadius: '10px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        border: '1px solid #E5E7EB',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#E5E7EB'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#E3F2FD'
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#9CA3AF',
                                        fontSize: '14px',
                                        transform: 'translate(14px, 10px) scale(1)',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, -6px) scale(0.75)'
                                        },
                                        '&.Mui-focused': {
                                            color: '#9CA3AF'
                                        }
                                    }
                                }}
                            >
                                <InputLabel>
                                    <Trans>Type</Trans>
                                </InputLabel>
                                <Select
                                    value={toggleValue}
                                    onChange={handleDropdownChange}
                                    label={t`Cohort Type`}
                                    sx={{
                                        color: '#374151',
                                        fontWeight: 700,
                                        fontSize: '14px'
                                    }}
                                >
                                    <MenuItem value='NETWORK'>
                                        <Trans>Network</Trans>
                                    </MenuItem>
                                    <MenuItem value='CONDITION'>
                                        <Trans>Cohort</Trans>
                                    </MenuItem>
                                    <MenuItem value='CUSTOM'>
                                        <Trans>Custom</Trans>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        ) : (
                            // Toggle buttons for larger screens
                            <ToggleButtonGroup
                                exclusive
                                value={toggleValue}
                                onChange={handleToggleChange}
                                sx={{
                                    backgroundColor: '#F6F8FB',
                                    borderRadius: '10px',
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        px: 2,
                                        py: 1,
                                        fontSize: '14px',
                                        minWidth: '90px',
                                        border: '1px solid #E5E7EB',
                                        color: '#6B7280'
                                    },
                                    '& .MuiToggleButton-root.Mui-selected': {
                                        color: '#374151',
                                        backgroundColor: '#E3F2FD',
                                        '&:hover': {
                                            color: '#374151',
                                            backgroundColor: '#E3F2FD'
                                        }
                                    }
                                }}
                                size='large'
                            >
                                <ToggleButton value='NETWORK'>
                                    <Trans>Network</Trans>
                                </ToggleButton>
                                <ToggleButton value='CONDITION'>
                                    <Trans>Cohort</Trans>
                                </ToggleButton>
                                <ToggleButton value='CUSTOM'>
                                    <Trans>Custom</Trans>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {/* Hidden measurement box to compute required width for toggle buttons */}
                        <Box ref={toggleMeasureRef} sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}>
                            <ToggleButtonGroup
                                value={'CUSTOM'}
                                sx={{
                                    backgroundColor: '#F6F8FB',
                                    borderRadius: '10px',
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        px: 2,
                                        py: 1,
                                        fontSize: '14px',
                                        minWidth: '90px',
                                        border: '1px solid #E5E7EB',
                                        color: '#6B7280'
                                    }
                                }}
                            >
                                <ToggleButton value='NETWORK'>
                                    <Trans>Network</Trans>
                                </ToggleButton>
                                <ToggleButton value='CONDITION'>
                                    <Trans>Cohort</Trans>
                                </ToggleButton>
                                <ToggleButton value='CUSTOM'>
                                    <Trans>Custom</Trans>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                    <Box px={isSmallScreen ? 0.5 : 0}>
                        <DebounceInput
                            element={SearchField}
                            minLength={1}
                            width={searchWidth}
                            debounceTimeout={500}
                            value={searchValue || ''}
                            onChange={props.handleSearchChange}
                            placeholder={t`Search...`}
                        />
                    </Box>
                </Box>
                <Box
                    ref={cohortListRef}
                    display='flex'
                    flexDirection='column'
                    gap={1}
                    px={1}
                    pb={2}
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto'
                    }}
                >
                    {visibleCohorts.map((cohort: CohortListItem) => {
                        const isSelected = cohort.id === selectedCohort?.id
                        return (
                            <Box
                                key={cohort.id}
                                ref={(ref: HTMLDivElement) => {
                                    if (isSelected) {
                                        selectedCohortRef.current = ref
                                    }
                                }}
                            >
                                <DashboardCohortCard
                                    cohort={cohort}
                                    isSelected={isSelected}
                                    onDeleteView={handleDeleteView}
                                    onDuplicate={handleCohortDuplicate}
                                    reportType={props.reportType}
                                    onRefreshSize={handleRefreshCount}
                                    onUpdateCohort={props.onUpdateCohort}
                                    sizeLoading={cohortSizeLoading[cohort.id]}
                                    setCohortId={setCohortId}
                                    trackPage={props.trackPage}
                                />
                            </Box>
                        )
                    })}
                    {searchValue && visibleCohorts.length === 0 && (
                        <ErrorCard>
                            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} alignContent={'center'} justifyContent={'center'}>
                                <SearchOffIcon sx={{ width: '50px', height: '50px' }} />
                                <Typography sx={{ margin: '20px', fontWeight: '800' }}>
                                    <Trans>No results found.</Trans>
                                </Typography>
                            </Box>
                        </ErrorCard>
                    )}
                    {!cohortsLoading &&
                        !searchValue &&
                        visibleCohorts.length === 0 &&
                        (props.selectedOwner === DashboardCohortOwner.DATASET ? (
                            <ErrorCard>
                                <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                                    <Typography sx={{ margin: '20px', fontWeight: '800' }}>
                                        <Trans>No datasets available.</Trans>
                                    </Typography>
                                </Box>
                            </ErrorCard>
                        ) : (
                            <ErrorCard>
                                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} alignContent={'center'} justifyContent={'center'}>
                                    <Button onClick={props.onCreateCohortClick} disabled={!props.onCreateCohortClick}>
                                        <AddCircleOutlineIcon sx={{ width: '50px', height: '50px', marginLeft: '10px' }} />
                                        <Typography sx={{ margin: '20px', fontWeight: '400' }}>
                                            <Trans>Create your first cohort</Trans>
                                        </Typography>
                                    </Button>
                                </Box>
                            </ErrorCard>
                        ))}
                    {cohortsLoading && !searchValue && (
                        <Box display={'flex'} flexDirection={'row'} alignItems={'center'} alignContent={'center'} justifyContent={'center'}>
                            <CircularProgress sx={{ color: '#012D72' }} />
                        </Box>
                    )}
                    {!cohortsLoading && props.selectedOwner === DashboardCohortOwner.CUSTOM && (
                        <Box ref={autoScrollRef} height={'10px'} width={'auto'} />
                    )}
                </Box>
            </Box>
            {deleteModalCohort && (
                <DeleteCohortDialog
                    id={deleteModalCohort.id}
                    searchValue={searchValue}
                    page={page}
                    actions={props.actions}
                    onCancel={handleDeleteHide}
                    setCohortId={setCohortId}
                    selectedCohort={selectedCohort}
                />
            )}
        </Box>
    )
}
